import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator, ChevronRight, ShieldCheck, Sparkles,
  Hammer, Wrench, Home, PaintBucket, Zap, Flame, Layers,
  Package, DoorOpen, Snowflake, Building2, TreePine, Ruler,
} from "lucide-react";

const CATEGORY_ICONS = {
  "renovation-complete": Home, "cuisine": Package, "salle-de-bain": Home,
  "plomberie": Wrench, "electricite": Zap, "chauffage-clim": Snowflake,
  "peinture-murs": PaintBucket, "sol-parquet": Layers, "carrelage": Layers,
  "menuiserie": DoorOpen, "isolation": Flame, "toiture": TreePine,
  "extension": Building2, "gros-oeuvre": Hammer,
};

const fmtEUR = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n) + " €";

export default function Estimator() {
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selCategory, setSelCategory] = useState(null);
  const [surface, setSurface] = useState(20);
  const [city, setCity] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);

  useEffect(() => {
    apiClient.get("/estimator/categories").then(({ data }) => setCategories(data.categories || []));
  }, []);

  const cat = useMemo(() => categories.find(c => c.slug === selCategory), [categories, selCategory]);

  const compute = async () => {
    if (!selCategory || !city.trim()) return;
    setLoading(true);
    try {
      const { data } = await apiClient.post("/estimator/estimate", {
        category: selCategory, surface: Number(surface), city: city.trim(),
      });
      setEstimate(data);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/estimator/leads", {
        category: selCategory, surface: Number(surface), city: city.trim(),
        estimate_min: estimate.estimate.min, estimate_max: estimate.estimate.max,
        ...lead,
      });
      setLeadSent(true);
      toast.success("Demande envoyée — un conseiller vous recontacte sous 24h");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F9] flex flex-col" data-testid="estimator-page">
      <SEO cfg={getSEO("/estimer")} />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5" /> Gratuit · Sans inscription
            </div>
            <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
              Estimateur travaux BTP <span className="text-[#FF4D00]">gratuit</span> · Prix rénovation en 2 min
            </h1>
            <p className="mt-6 text-zinc-300 text-base max-w-xl">
              Estimation instantanée du prix de vos travaux (rénovation salle de bain, cuisine, extension, isolation, plomberie, électricité, peinture, toiture) basée sur les tarifs moyens du marché français 2026, ajustée à votre ville. Puis recevez <strong className="text-white">3 devis gratuits</strong> d'artisans SIRET vérifiés Batisur.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 text-xs mono uppercase tracking-widest text-zinc-400">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FF4D00]" /> Escrow Stripe</span>
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF4D00]" /> SIRET vérifié</span>
              <span className="flex items-center gap-2"><Ruler className="w-4 h-4 text-[#FF4D00]" /> Prix 2026</span>
            </div>
          </div>
          <div className="hidden lg:flex justify-end">
            <div className="border-2 border-white/20 p-8 max-w-sm bg-[#FF4D00]/10">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Exemple</div>
              <div className="display text-2xl mt-3">Salle de bain 6 m² à Paris</div>
              <div className="display text-5xl tracking-tighter text-white mt-4">4 680 – 17 160 €</div>
              <div className="mono text-[10px] text-zinc-400 mt-2">Fourchette réaliste marché 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stepper */}
      <section className="max-w-5xl mx-auto px-6 py-12 w-full flex-1">
        <div className="flex items-center justify-between mb-8 overflow-x-auto">
          {[
            { n: 1, l: "Type de travaux" }, { n: 2, l: "Surface & ville" }, { n: 3, l: "Estimation" },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <div className={`w-9 h-9 border-2 flex items-center justify-center font-bold text-sm shrink-0 ${step >= s.n ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "border-zinc-300 text-zinc-400 bg-white"}`}>
                {s.n}
              </div>
              <div className={`mono text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-widest truncate ${step >= s.n ? "text-[#0A0A0B]" : "text-zinc-400"}`}>
                {s.l}
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 mx-1 sm:mx-2 min-w-[8px] ${step > s.n ? "bg-[#FF4D00]" : "bg-zinc-300"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: category */}
        {step === 1 && (
          <div data-testid="step-category">
            <h2 className="display text-3xl tracking-tighter mb-6">Quel type de travaux ?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {categories.map((c) => {
                const Icon = CATEGORY_ICONS[c.slug] || Hammer;
                const active = selCategory === c.slug;
                return (
                  <button
                    key={c.slug}
                    onClick={() => { setSelCategory(c.slug); setStep(2); }}
                    data-testid={`category-${c.slug}`}
                    className={`text-left border-2 p-5 transition ${active ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : "border-[#0A0A0B] bg-white hover:bg-[#FF4D00] hover:text-white"}`}
                  >
                    <Icon className="w-6 h-6 mb-3" strokeWidth={2} />
                    <div className="display text-lg tracking-tight">{c.label}</div>
                    <div className="text-xs mt-1 opacity-80">{c.description}</div>
                    <div className="mono text-[10px] uppercase tracking-widest mt-3 opacity-70">
                      ~{fmtEUR(c.avg)} / {c.unit}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: surface + city */}
        {step === 2 && cat && (
          <div data-testid="step-surface" className="max-w-2xl">
            <button className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] mb-4" onClick={() => { setStep(1); setEstimate(null); }}>← Changer de travaux</button>
            <h2 className="display text-3xl tracking-tighter mb-2">{cat.label}</h2>
            <p className="text-sm text-zinc-600 mb-8">{cat.description}</p>

            <label className="block mb-6">
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-600 mb-2">Surface / Quantité ({cat.unit})</div>
              <div className="flex items-center gap-4">
                <input
                  type="range" min="1" max={cat.unit === "m²" ? 500 : cat.unit === "logement" ? 5 : 50}
                  value={surface} onChange={(e) => setSurface(e.target.value)}
                  className="flex-1 accent-[#FF4D00]" data-testid="surface-slider"
                />
                <input
                  type="number" min="1" value={surface} onChange={(e) => setSurface(e.target.value)}
                  className="w-24 border-2 border-[#0A0A0B] px-3 py-2 text-center font-bold" data-testid="surface-input"
                />
                <span className="mono text-xs uppercase tracking-widest text-zinc-500">{cat.unit}</span>
              </div>
            </label>

            <label className="block mb-8">
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-600 mb-2">Votre ville</div>
              <input
                type="text" placeholder="Paris, Lyon, Marseille, Limoges…"
                value={city} onChange={(e) => setCity(e.target.value)}
                className="w-full border-2 border-[#0A0A0B] px-4 py-3" data-testid="city-input"
              />
            </label>

            <button
              onClick={compute}
              disabled={!city.trim() || loading}
              className="btn-batisur disabled:opacity-40" data-testid="compute-btn"
            >
              {loading ? "Calcul…" : "Voir mon estimation"} <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {/* Step 3: results */}
        {step === 3 && estimate && (
          <div data-testid="step-result">
            <button className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] mb-4" onClick={() => setStep(2)}>← Modifier</button>
            <div className="border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-8 lg:p-12 mb-8">
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Votre estimation</div>
              <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-3">
                {estimate.category.label} · {estimate.surface} {estimate.category.unit} · {estimate.city}
              </h2>
              <div className="mt-6 grid grid-cols-3 gap-0 border-2 border-white/20">
                <div className="p-6 border-r-2 border-white/20">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">Bas de gamme</div>
                  <div className="display text-3xl mt-2 text-white/70">{fmtEUR(estimate.estimate.min)}</div>
                </div>
                <div className="p-6 border-r-2 border-white/20 bg-[#FF4D00]">
                  <div className="mono text-[10px] uppercase tracking-widest text-white/90">Prix moyen</div>
                  <div className="display text-4xl mt-2 text-white">{fmtEUR(estimate.estimate.avg)}</div>
                </div>
                <div className="p-6">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">Haut de gamme</div>
                  <div className="display text-3xl mt-2 text-white/70">{fmtEUR(estimate.estimate.max)}</div>
                </div>
              </div>
              {estimate.duration_days_estimated && (
                <div className="mt-4 mono text-[11px] uppercase tracking-widest text-zinc-400">
                  Durée estimée : ~{estimate.duration_days_estimated} jours de chantier
                </div>
              )}
              <div className="mt-4 mono text-[11px] uppercase tracking-widest text-zinc-400">
                Multiplicateur ville · ×{estimate.city_multiplier.toFixed(2)}
              </div>
              <p className="text-xs text-zinc-500 mt-6 max-w-2xl leading-relaxed">{estimate.disclaimer}</p>
            </div>

            {/* Lead capture */}
            {!leadSent ? (
              <div className="border-2 border-[#FF4D00] bg-white p-8" data-testid="lead-form-wrapper">
                <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Action recommandée</div>
                <h3 className="display text-2xl lg:text-3xl tracking-tighter mt-2">Recevez 3 devis gratuits d'artisans vérifiés</h3>
                <p className="text-sm text-zinc-600 mt-2 max-w-2xl">Sans engagement · SIRET vérifié Sirene/INSEE · Paiement sécurisé en séquestre Stripe · Réponse sous 24h</p>
                <form onSubmit={submitLead} className="grid md:grid-cols-2 gap-4 mt-6">
                  <input required placeholder="Prénom et nom" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="border-2 border-[#0A0A0B] px-4 py-3" data-testid="lead-name" />
                  <input required type="email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="border-2 border-[#0A0A0B] px-4 py-3" data-testid="lead-email" />
                  <input placeholder="Téléphone (optionnel)" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="border-2 border-[#0A0A0B] px-4 py-3" data-testid="lead-phone" />
                  <input placeholder="Précisez votre projet (optionnel)" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="border-2 border-[#0A0A0B] px-4 py-3" data-testid="lead-message" />
                  <button type="submit" className="btn-batisur md:col-span-2" data-testid="lead-submit">Recevoir mes 3 devis gratuits →</button>
                </form>
                <div className="mt-4 mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Ou <Link to="/register?role=demandeur" className="text-[#FF4D00] underline">créez votre compte</Link> pour lancer directement une mission
                </div>
              </div>
            ) : (
              <div className="border-2 border-emerald-600 bg-emerald-50 p-8 text-center" data-testid="lead-sent">
                <ShieldCheck className="w-10 h-10 mx-auto text-emerald-600" />
                <h3 className="display text-2xl mt-3">Demande envoyée ✓</h3>
                <p className="mt-2 text-sm text-emerald-800 max-w-md mx-auto">
                  Un conseiller Batisur vous recontacte sous 24h avec 3 devis d'artisans vérifiés de votre ville. Pas d'engagement.
                </p>
                <Link to="/artisans" className="btn-anthracite inline-block mt-6" data-testid="lead-sent-cta">Explorer les artisans en attendant</Link>
              </div>
            )}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
